import type { Weather } from './engine';

export type HolidayTheme = 'halloween'|'christmas'|'new-year'|'valentine'|'canada-day'|'winter'|'none';
export type RecoverySurface = 'mud'|'snow'|'ice'|'sand'|'ditch'|'gravel'|'grass'|'leaves';
export type RecoveryCover = 'none'|'wet'|'snow';

export type SeasonalState = {
  year:number;
  month:number;
  day:number;
  winter:boolean;
  holiday:HolidayTheme;
  icon:string;
  label:string;
};

const SNOW_ONLY = new Set(['winch_snowbank','winch_icy_driveway','winch_snowplow_ridge','winch_ice_hollow','winch_garage_snowdrift']);
const WINTER_ONLY = new Set(['jump_cold_morning','winch_thawing_shoulder']);
const WET_RECOVERIES = new Set([
  'winch_muddy_field','winch_campground_grass','winch_farm_clay','winch_trail_shoulder',
  'winch_leaf_slope','winch_roadside_ditch','winch_construction_ruts','winch_drainage_dip',
  'winch_sports_ground','winch_washed_out_track','winch_thawing_shoulder','winch_hay_field_exit',
  'winch_silt_pullout','winch_trail_bog','winch_picnic_bank',
  'heavy_logging_rig_mud','heavy_box_trailer_mud','heavy_tanker_soft_shoulder','heavy_flatbed_farm_track',
  'heavy_refrigerated_trailer','heavy_gravel_pit_rig','heavy_moving_rig_mud','heavy_livestock_rig_empty',
  'heavy_container_rig','heavy_curtain_rig',
]);
const DRY_RECOVERIES = new Set(['winch_beach_sand','winch_sand_trap','winch_spilled_sand','winch_dry_creek_stones']);

export const RECOVERY_SURFACES:Record<string,RecoverySurface> = {
  winch_snowbank:'snow',
  winch_icy_driveway:'ice',
  winch_beach_sand:'sand',
  winch_muddy_field:'mud',
  winch_campground_grass:'grass',
  winch_boat_ramp_edge:'ditch',
  winch_farm_clay:'mud',
  winch_trail_shoulder:'mud',
  winch_leaf_slope:'leaves',
  winch_roadside_ditch:'ditch',
  winch_crumbling_verge:'gravel',
  winch_construction_ruts:'gravel',
  winch_drainage_dip:'ditch',
  winch_curb_high_center:'gravel',
  winch_yard_gravel:'gravel',
  winch_garden_bed:'grass',
  winch_sports_ground:'grass',
  winch_sand_trap:'sand',
  winch_nursery_soil:'grass',
  winch_snowplow_ridge:'snow',
  winch_ice_hollow:'ice',
  winch_washed_out_track:'ditch',
  winch_hidden_trench:'ditch',
  winch_paved_edge_drop:'ditch',
  winch_thawing_shoulder:'mud',
  winch_loose_switchback:'gravel',
  winch_hay_field_exit:'mud',
  winch_offroad_berm:'grass',
  winch_courtyard_dip:'ditch',
  winch_spilled_sand:'sand',
  winch_orchard_rut:'ditch',
  winch_dry_creek_stones:'gravel',
  winch_reverse_embankment:'ditch',
  winch_garage_snowdrift:'snow',
  winch_silt_pullout:'mud',
  winch_loose_landscaping:'gravel',
  winch_roadworks_ramp:'gravel',
  winch_trail_bog:'mud',
  winch_ash_path:'gravel',
  winch_picnic_bank:'ditch',
  heavy_logging_rig_mud:'mud',
  heavy_box_trailer_mud:'mud',
  heavy_tanker_soft_shoulder:'mud',
  heavy_flatbed_farm_track:'mud',
  heavy_refrigerated_trailer:'mud',
  heavy_gravel_pit_rig:'mud',
  heavy_moving_rig_mud:'mud',
  heavy_livestock_rig_empty:'mud',
  heavy_container_rig:'mud',
  heavy_curtain_rig:'mud',
};

function vancouverParts(now:Date){
  const parts=new Intl.DateTimeFormat('en-CA',{timeZone:'America/Vancouver',year:'numeric',month:'2-digit',day:'2-digit'}).formatToParts(now);
  const value=(type:string)=>Number(parts.find(part=>part.type===type)?.value??0);
  return{year:value('year'),month:value('month'),day:value('day')};
}

export function seasonalStateForDate(now=new Date()):SeasonalState{
  const {year,month,day}=vancouverParts(now);
  const winter=month===11||month===12||month===1;
  let holiday:HolidayTheme='none',icon='✦',label='Seasonal streets';
  if(month===10){holiday='halloween';icon='🎃';label='Halloween in Richmond';}
  else if(month===12&&day<=26){holiday='christmas';icon='🎄';label='Christmas in Richmond';}
  else if((month===12&&day>=27)||(month===1&&day<=7)){holiday='new-year';icon='✨';label='New Year in Richmond';}
  else if(month===2&&day>=8&&day<=14){holiday='valentine';icon='♥';label='Valentine week';}
  else if((month===6&&day>=28)||(month===7&&day<=2)){holiday='canada-day';icon='🍁';label='Canada Day week';}
  else if(winter){holiday='winter';icon='❄';label='Winter in Richmond';}
  return{year,month,day,winter,holiday,icon,label};
}

export function weatherForShift(now:Date,shiftNumber:number):Weather{
  const season=seasonalStateForDate(now);
  const serial=Math.max(1,Math.floor(shiftNumber||1));
  if(serial%4===0)return season.winter?'snow':'rain';
  if(serial%7===0)return'fog';
  return'clear';
}

export function requestAllowedForWeather(key:string,weather:Weather,season:SeasonalState){
  if(SNOW_ONLY.has(key))return weather==='snow';
  if(WINTER_ONLY.has(key))return season.winter||weather==='snow';
  if(weather==='snow'){
    if(WET_RECOVERIES.has(key)||DRY_RECOVERIES.has(key))return false;
  }
  if(DRY_RECOVERIES.has(key)&&weather==='rain')return false;
  return true;
}

export function requestWeatherWeight(key:string,weather:Weather){
  if(SNOW_ONLY.has(key))return weather==='snow'?7:0;
  if(WET_RECOVERIES.has(key)){
    if(weather==='rain')return 4.8;
    if(weather==='fog')return 1.5;
    if(weather==='clear')return .58;
    return 0;
  }
  if(DRY_RECOVERIES.has(key))return weather==='clear'?2.1:weather==='fog'?1.15:.35;
  if(key==='jump_cold_morning')return weather==='snow'?3.4:1.2;
  return 1;
}

export function recoverySurfaceForRequest(key:string,weather:Weather):RecoverySurface{
  // Weather is a cover on the scene, not a replacement for its geometry. A
  // snow-covered trench must still read as a trench and a verge as gravel.
  void weather;
  return RECOVERY_SURFACES[key]??'grass';
}

const WETTABLE_RECOVERY_SURFACES:ReadonlySet<RecoverySurface>=new Set(['mud','ditch','gravel','grass','leaves']);

export function recoveryCoverForRequest(key:string,weather:Weather):RecoveryCover{
  if(weather==='snow')return'snow';
  if(weather==='rain'&&WETTABLE_RECOVERY_SURFACES.has(recoverySurfaceForRequest(key,weather)))return'wet';
  return'none';
}

export function holidayDecorEnabled(theme:HolidayTheme){
  return theme!=='none'&&theme!=='winter';
}
